import React, { useCallback, useEffect, useRef, useState } from 'react';
import { t } from 'i18next';
import { AxiosInstance } from 'axios';
import { Bars } from 'react-loader-spinner';

import { useAppSelector } from '../../../hooks/hooksRedux';
import { RootState } from '../../../store';
import { defaultColors } from '../../../styles/style-guide';
import { useApi } from '../../../services/hooks/apiHook';
import { ServiceBaseConfig } from '../../../services/Interfaces/IAccountService';
import { CompanyLogoService } from '../../../services/CompanyLogoService';
import { errorMessageAdapter } from '../../../adapters';
import { errorToast, successToast } from '../../../utils/toasts';
import { companyLogoUrl } from '../../../utils/companyLogoUrl';
import { isCompanyLogoShownInMenu, setCompanyLogoShownInMenu } from '../../../utils/companyLogoMenu';

const MAX_LOGO_BYTES = 5 * 1024 * 1024;
const ALLOWED_TYPES = ['image/jpeg', 'image/jpg', 'image/png'];

interface CompanyLogoUploadProps {
  companyId?: number;
  logoPath?: string | null;
  canUpload: boolean;
  headers: ServiceBaseConfig['headers'];
  onUploaded: () => Promise<unknown> | void;
}

export const CompanyLogoUpload: React.FC<CompanyLogoUploadProps> = ({
  companyId,
  logoPath,
  canUpload,
  headers,
  onUploaded
}) => {
  const { colors } = useAppSelector((state: RootState) => state.styleGuide.data);
  const primaryColor = colors?.primary || defaultColors.primary;
  const api: AxiosInstance = useApi();
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const [isUploading, setIsUploading] = useState(false);
  const [showInMenu, setShowInMenu] = useState(() => isCompanyLogoShownInMenu(companyId));

  useEffect(() => {
    setShowInMenu(isCompanyLogoShownInMenu(companyId));
  }, [companyId]);
  const fileInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    return () => {
      if (previewUrl) {
        URL.revokeObjectURL(previewUrl);
      }
    };
  }, [previewUrl]);

  const clearPreview = useCallback(() => {
    setPreviewUrl((current) => {
      if (current) {
        URL.revokeObjectURL(current);
      }
      return null;
    });
  }, []);

  const uploadFile = useCallback(
    async (file: File) => {
      if (!companyId || !canUpload || isUploading) {
        return;
      }

      if (!ALLOWED_TYPES.includes(file.type)) {
        errorToast(t('labels.lblUnsupportedFileFormat') || '');
        return;
      }

      if (file.size > MAX_LOGO_BYTES) {
        errorToast(t('labels.lblFileTooLarge') || '');
        return;
      }

      const localPreview = URL.createObjectURL(file);
      setPreviewUrl((current) => {
        if (current) {
          URL.revokeObjectURL(current);
        }
        return localPreview;
      });
      setIsUploading(true);

      try {
        const response = await CompanyLogoService.uploadCompanyLogo(api, file, { headers });

        if (!response?.success) {
          clearPreview();
          errorToast(t('labels.lblUploadError') || '');
          return;
        }

        successToast(t('labels.lblLogoUpdatedSuccess') || '');
        await onUploaded();
        clearPreview();
      } catch (error: any) {
        clearPreview();
        if (error?.response?.status === 403) {
          errorToast(t('labels.lblCompanyNotApproved') || '');
          return;
        }
        errorToast(errorMessageAdapter(error));
      } finally {
        setIsUploading(false);
      }
    },
    [api, canUpload, clearPreview, companyId, headers, isUploading, onUploaded]
  );

  const handleFileInput = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      const file = event.target.files?.[0];
      event.target.value = '';
      if (file) {
        void uploadFile(file);
      }
    },
    [uploadFile]
  );

  const handleDrop = useCallback(
    (event: React.DragEvent<HTMLDivElement>) => {
      event.preventDefault();
      const file = event.dataTransfer.files?.[0];
      if (file) {
        void uploadFile(file);
      }
    },
    [uploadFile]
  );

  if (!companyId) {
    return null;
  }

  const displayImage = previewUrl || companyLogoUrl(logoPath);
  const openFilePicker = () => {
    if (!isUploading) {
      fileInputRef.current?.click();
    }
  };

  const fileInput = (
    <input
      ref={fileInputRef}
      type="file"
      className="sr-only"
      accept=".jpg,.jpeg,.png,image/jpeg,image/png"
      onChange={handleFileInput}
      disabled={isUploading}
    />
  );

  return (
    <div className="mt-4 rounded-md border bg-white p-4 shadow-sm">
      <div className="flex items-center gap-4">
        <div className="relative shrink-0">
          {displayImage ? (
            <img
              src={displayImage}
              alt={t('labels.lblCompanyLogo') || ''}
              className="h-16 w-16 rounded-xl border border-gray-200 bg-white object-contain p-1.5"
            />
          ) : (
            <div className="flex h-16 w-16 items-center justify-center rounded-xl border border-gray-200 bg-slate-50">
              <svg className="h-8 w-8 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={1.5}
                  d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4"
                />
              </svg>
            </div>
          )}
          {isUploading && (
            <div className="absolute inset-0 flex items-center justify-center rounded-xl bg-white/80">
              <Bars height="24" width="24" color={primaryColor} visible />
            </div>
          )}
        </div>

        <div className="min-w-0 flex-1">
          <h3 className="text-lg font-bold text-[#0C1FAA]">{t('labels.lblCompanyLogo')}</h3>
          <p className="mt-1 text-sm text-gray-500">
            {canUpload
              ? displayImage
                ? `${t('labels.lblLogoFormats')} · ${t('labels.lblLogoSizeLimit')}`
                : t('labels.lblCompanyLogoDescription')
              : t('labels.lblCompanyNotApproved')}
          </p>
        </div>

        {canUpload && displayImage && (
          <button
            type="button"
            onClick={openFilePicker}
            disabled={isUploading}
            className="shrink-0 rounded-md px-4 py-2 text-sm font-medium text-white disabled:cursor-not-allowed disabled:opacity-60"
            style={{ backgroundColor: primaryColor }}
          >
            {isUploading ? t('labels.lblUploadingLogo') : t('labels.lblChangeLogo')}
          </button>
        )}
      </div>

      {displayImage && companyId && (
        <label className="mt-3 flex w-fit cursor-pointer items-center gap-2 text-sm text-gray-600">
          <input
            type="checkbox"
            checked={showInMenu}
            onChange={(event) => {
              const next = event.target.checked;
              setShowInMenu(next);
              setCompanyLogoShownInMenu(companyId, next);
            }}
            style={{ accentColor: primaryColor }}
          />
          {t('labels.lblShowLogoInMenu')}
        </label>
      )}

      {canUpload && !displayImage && (
        <div
          className={`mt-4 rounded-lg border-2 border-dashed p-6 text-center transition-colors ${
            isUploading ? 'cursor-not-allowed bg-slate-50' : 'cursor-pointer hover:bg-slate-50'
          }`}
          style={{ borderColor: primaryColor }}
          onDrop={handleDrop}
          onDragOver={(event) => event.preventDefault()}
          onClick={openFilePicker}
        >
          {isUploading ? (
            <div className="flex flex-col items-center">
              <Bars height="36" width="36" color={primaryColor} visible />
              <p className="mt-3 text-sm text-gray-600">{t('labels.lblUploadingLogo')}</p>
            </div>
          ) : (
            <>
              <p className="text-sm text-gray-600">
                <span className="font-medium" style={{ color: primaryColor }}>
                  {t('labels.lblClickToSelect')}
                </span>{' '}
                {t('labels.lblOrDragImageHere')}
              </p>
              <p className="mt-2 text-xs text-gray-400">
                {t('labels.lblLogoRecommendations')} · {t('labels.lblLogoFormats')} · {t('labels.lblLogoSizeLimit')}
              </p>
            </>
          )}
        </div>
      )}

      {canUpload && fileInput}
    </div>
  );
};
